import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ServiceCalendar, AssignedSlots } from "@/components/ServiceCalendar";
import { AvailabilityReminder } from "@/components/AvailabilityReminder";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Clock, MapPin, Loader2, X, CalendarPlus, Pencil, Info } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { BulkServiceDialog } from "@/components/BulkServiceDialog";

const emptyService = { date: "", start_time: "", end_time: "", name: "", location: "", notes: "", slots: [] };

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyService);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, a] = await Promise.all([
        api.get("/services"),
        api.get(`/admin/service-types`),
        api.get("/planning/assignments"),
      ]);
      setServices(s.data);
      setTypes(t.data);
      setAssignments(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addSlot = () => setForm((f) => ({ ...f, slots: [...f.slots, { service_type_id: "", desired: 1, min: 1, max: 0 }] }));
  const setSlot = (i, k, v) => setForm((f) => {
    const slots = [...f.slots]; slots[i] = { ...slots[i], [k]: v }; return { ...f, slots };
  });
  const removeSlot = (i) => setForm((f) => ({ ...f, slots: f.slots.filter((_, x) => x !== i) }));

  const openEdit = (s) => {
    setForm({ date: s.date, start_time: s.start_time, end_time: s.end_time, name: s.name,
      location: s.location || "", notes: s.notes || "",
      slots: (s.slots || []).map((sl) => ({ service_type_id: sl.service_type_id, desired: sl.desired, min: sl.min ?? 0, max: sl.max ?? 0 })) });
    setEditingId(s.id); setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const slots = form.slots.filter((s) => s.service_type_id).map((s) => {
        const t = types.find((x) => x.id === s.service_type_id);
        return { service_type_id: s.service_type_id, service_type_name: t?.name || "", desired: parseInt(s.desired, 10) || 1, min: parseInt(s.min, 10) || 0, max: parseInt(s.max, 10) || 0 };
      });
      if (editingId) {
        await api.put(`/services/${editingId}`, { ...form, slots });
        toast.success("Dienst bijgewerkt");
      } else {
        await api.post("/services", { ...form, slots });
        toast.success("Dienst aangemaakt");
      }
      setOpen(false); setForm(emptyService); setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Dienst verwijderen?")) return;
    try { await api.delete(`/services/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Diensten</h1>
          <p className="text-muted-foreground mt-1" data-testid="services-count">Doorlopende planning — {services.length} diensten</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-service-btn"><CalendarPlus className="w-4 h-4 mr-1.5" /> Bulk toevoegen</Button>
          <Button onClick={() => { setForm(emptyService); setEditingId(null); setOpen(true); }} data-testid="new-service-btn"><Plus className="w-4 h-4 mr-1.5" /> Nieuwe dienst</Button>
        </div>
      </div>

      {!services.length && !loading && (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="services-empty-setup">Voeg je eerste dienst toe, los of in bulk. Voor functies voeg je als beheerder werkzaamheden toe via Instellingen.</Card>
      )}
      <ServiceCalendar services={services} calendarId="services" loading={loading}
        getStatus={(s) => { const st = s.publish_state || (s.published ? "provisional" : "concept"); return st === "final" ? { tone: "red", label: "Definitief" } : st === "provisional" ? { tone: "amber", label: "Voorlopig" } : { tone: "slate", label: "Concept" }; }}
        getNames={(s) => assignments.filter((a) => a.service_id === s.id).map((a) => a.user_name)}
        onCreateDate={(date) => { setForm({ ...emptyService, date }); setEditingId(null); setOpen(true); }}
        renderDetails={(s) => (
              <Card key={s.id} className="p-4" data-testid={`service-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Clock className="w-3 h-3" /> {s.start_time}–{s.end_time}
                      {s.location && <><MapPin className="w-3 h-3 ml-1" /> {s.location}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {s.notes && <p className="text-xs text-amber-700 mt-2 flex items-start gap-1" data-testid={`service-note-${s.id}`}><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{s.notes}</p>}
                <div className="mt-3">
                  <AssignedSlots slots={s.slots} assignments={assignments.filter((a) => a.service_id === s.id)} />
                </div>
                <AvailabilityReminder service={s} source="services" />
              </Card>
        )} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Dienst bewerken" : "Nieuwe dienst"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label>Naam</Label><Input value={form.name} onChange={set("name")} data-testid="service-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Datum</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="service-date" /></div>
              <div className="space-y-1.5"><Label>Begin</Label><Input type="time" value={form.start_time} onChange={set("start_time")} data-testid="service-start" /></div>
              <div className="space-y-1.5"><Label>Eind</Label><Input type="time" value={form.end_time} onChange={set("end_time")} data-testid="service-end" /></div>
            </div>
            <div className="space-y-1.5"><Label>Locatie</Label><Input value={form.location} onChange={set("location")} data-testid="service-location" /></div>
            <div className="space-y-1.5"><Label>Opmerkingen</Label><Textarea value={form.notes} onChange={set("notes")} placeholder="Bijv. deze avond is er een activiteit" rows={2} maxLength={500} data-testid="service-notes" /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Functies</Label>
                <Button variant="outline" size="sm" onClick={addSlot} data-testid="add-slot"><Plus className="w-3.5 h-3.5 mr-1" /> Functie</Button>
              </div>
              <div className="space-y-2">
                {form.slots.map((sl, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`slot-row-${i}`}>
                    <Select value={sl.service_type_id} onValueChange={(v) => setSlot(i, "service_type_id", v)}>
                      <SelectTrigger className="flex-1" data-testid={`slot-type-${i}`}><SelectValue placeholder="Werkzaamheid" /></SelectTrigger>
                      <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" className="w-16" value={sl.desired} onChange={(e) => setSlot(i, "desired", e.target.value)} title="Gewenst" data-testid={`slot-desired-${i}`} />
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-rose-600" onClick={() => removeSlot(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {form.slots.length === 0 && <p className="text-xs text-muted-foreground">Voeg minstens één functie toe.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }}>Annuleren</Button>
            <Button onClick={save} disabled={busy || !form.name || !form.date || !form.start_time || !form.end_time} data-testid="service-save">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingId ? "Opslaan" : "Aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkServiceDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        types={types}
        onDone={load}
      />
    </div>
  );
}
